import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { Inject } from '@odinjs/odin';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ActionAPI, FormAPI, InputAPI, PanelAPI, ToastAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import moment from 'moment';
import './diagnosis-editor.css';
import { SimpleDialog } from '../../dialog-helper';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../constants/routes';
import Types from '@philips-internal/oswald-ui-constants/lib/type';
import { AbortableSubmitEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import DiagnosisService, { DiagnosisResponseDTO } from '../diagnosis-service';
import { HttpStatus } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import DiagnosisMenu from '../diagnosis-menu/diagnosis-menu-controller';
import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import ShowToast from '../../../utils/toast-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { DiagnosisEditorAliases, FragmentNames } from '../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import timeService from '../../../services/time-service';

interface DiagnosisVO {
  diagnosisDescription: string;
  diseaseCode: string;
}

interface DomainVO {
  description: string;
  id: string;
}

interface DiagnosisDTO {
  classification?: DomainVO;
  description: string;
  diagnosis?: DiagnosisVO;
  diagnosisDate: string;
  diagnosisType?: DomainVO;
  diseaseType?: DomainVO;
  encounterId: string;
  sequenceNumber: string;
  side?: DomainVO;
}

@Controller({ fragment: FragmentNames.DIAGNOSIS_EDITOR })
export default class DiagnosisEditorController extends DiagnosisMenu {

  @Inject(DiagnosisEditorAliases.FORM_DIAGNOSIS)
    formApi!: FormAPI;

  @Inject(DiagnosisEditorAliases.PANEL_RELEASE)
    panelReleaseApi!: PanelAPI;

  @Inject(DiagnosisEditorAliases.INPUT_DATETIME)
    inputDateTimeApi!: InputAPI;

  @Inject(DiagnosisEditorAliases.TOAST_WARNING)
    toastWarnigApi!: ToastAPI;

  @OnMount(DiagnosisEditorAliases.ACTION_DELETE_DIAGNOSIS)
  handleDeleteClinicalNoteActionMount(event: MountEvent): void {
    const { status } = this.historyService.getCurrentRouteParams();
    const actionDeleteDiagnosis = event.target as ActionAPI;

    actionDeleteDiagnosis.setEnabled(!!status);
  }

  @OnMount(DiagnosisEditorAliases.FORM_DIAGNOSIS)
  handleMountFormDiagnosis(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, sequenceNumber, encounterId, status } = this.historyService.getCurrentRouteParams();
    this.context.set({
      edit: status,
      encounterId,
      patientId,
      sequenceNumber
    });
    this.diagnosisService = new DiagnosisService(this);
    this.inputDateTimeApi.setEnabled(true);

    if (status) {
      this.formApi.setLoading(true)
      HttpService.get(Endpoint.GET_DIAGNOSIS_EDIT, {
        encounterId,
        patientId,
        sequenceNumber
      }).then(response => {
        const res = response.body as Record<string, Value>;
        const record = { ...this.formApi.getData(), ...res } as Record<string, Value>;
        this.formApi.setData(record);
      })
        .catch(err => ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR))
        .finally(() => this.formApi.setLoading(false));

      this.panelReleaseApi.setVisible(true);
    } else {
      this.getCurrentDatetime();
      this.formApi.setDisabled(false);
    }
  }

  private getCurrentDatetime(): void {
    timeService.getCurrentDateTime()
      .then(date => {
        this.context.set({ currentDatetime: moment(date)})
        this.inputDateTimeApi.setValue(date)
      })
      .catch(() => this.inputDateTimeApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  @OnChange(DiagnosisEditorAliases.FORM_DIAGNOSIS)
  handleChangeFormDiagnosis(): void {
    const editSubmit = this.context.get('edit') as boolean;
    if (editSubmit) {
      this.panelReleaseApi.setVisible(false);
      this.formApi.setDisabled(false);
    }
  }

  @OnBeforeCancel(DiagnosisEditorAliases.FORM_DIAGNOSIS)
  handleBeforeCancelFormDiagnosis(): void {

    const record = this.formApi.getRecord();
    this.formApi.setRecord(record);
    this.formApi.setDisabled(true);

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'clinical-notes.discard-changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.cancelDiscard(record) },
      onClickOk: () => { this.discard() },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit',
    }
    SimpleDialog.openDialog(dialogParams);
  }

  @OnBeforeSubmit(DiagnosisEditorAliases.FORM_DIAGNOSIS)
  handleBeforeSubmitFormDiagnosis(event: AbortableSubmitEvent): void {
    event.abort();
    this.validateSaveDiagnosis(event)
  }

  private validateSaveDiagnosis(event: AbortableSubmitEvent): void {
    const diagnosis = event.record as DiagnosisDTO;
    const diagnosisDate = moment(diagnosis.diagnosisDate)

    const attendanceNumber = this.context.get('encounterId') as string;
    const personCode = this.context.get('patientId') as string;
    const editSubmit = this.context.get('edit') as boolean;
    const currentDatetime = this.context.get('currentDatetime') as moment.Moment

    if (diagnosisDate.isAfter(currentDatetime)) {
      this.toastWarnigApi.setType(Types.WARNING);
      this.toastWarnigApi.setValue('warning.clinical-note.later-date');
      this.toastWarnigApi.setTimeOut(5000);
      this.toastWarnigApi.open();

      return;
    }

    const newDiagnosis = {
      ...diagnosis,
      diagnosisDate: diagnosisDate.utc().format(ISO_FULL_DATE_FORMAT),
      encounterId: attendanceNumber
    } as DiagnosisDTO

    if (editSubmit) {
      this.updateDiagnosis(newDiagnosis, personCode);
      return;
    }

    this.createDiagnosis(newDiagnosis, personCode);
  }

  private createDiagnosis(diagnosisDto: DiagnosisDTO, patientId: string): void {
    const body = {
      classification: diagnosisDto.classification,
      description: diagnosisDto.description,
      diagnosis: diagnosisDto.diagnosis,
      diagnosisDate: moment(String(diagnosisDto.diagnosisDate)).utc().format(ISO_FULL_DATE_FORMAT),
      diagnosisType: diagnosisDto.diagnosisType,
      diseaseType: diagnosisDto.diseaseType,
      encounterId: diagnosisDto.encounterId,
      side: diagnosisDto.side
    }

    HttpService.post(Endpoint.POST_DIAGNOSIS, { patientId }, { body }).then(response => {
      const responseBody = response.body as DiagnosisResponseDTO;
      const sequenceNumber = responseBody.sequenceNumber;
      const status = responseBody.status;
      this.inputDateTimeApi.setValue(DateFormatterUtil.timeStamp(String(body.diagnosisDate)))
      this.panelReleaseApi.setVisible(false);

      if (status) {
        this.formApi.setEditing(false);
        this.formApi.setDisabled(true);
        this.diagnosisService.confirmCancelAndRelease(patientId, sequenceNumber, diagnosisDto.encounterId);
        return;
      }
    }).catch(err => {
      ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR);
      this.formApi.setEditing(true);
    });
  }

  private updateDiagnosis(diagnosisDto: DiagnosisDTO, patientId: string): void {
    const sequenceNumber = this.context.get('sequenceNumber') as string;
    const body = {
      classification: diagnosisDto.classification,
      description: diagnosisDto.description,
      diagnosis: diagnosisDto.diagnosis,
      diagnosisDate: moment(String(diagnosisDto.diagnosisDate)).utc().format(ISO_FULL_DATE_FORMAT),
      diagnosisType: diagnosisDto.diagnosisType,
      diseaseType: diagnosisDto.diseaseType,
      encounterId: diagnosisDto.encounterId,
      sequenceNumber: sequenceNumber,
      side: diagnosisDto.side
    }

    HttpService.put(Endpoint.PUT_DIAGNOSIS, { patientId }, { body }).then(response => {
      if (response.status === HttpStatus.NO_CONTENT) {
        this.formApi.setEditing(false);
        this.formApi.setDisabled(true);
        this.diagnosisService.confirmCancelAndRelease(patientId, sequenceNumber, diagnosisDto.encounterId);
        return;
      }
    }).catch(err => {
      ShowToast.open(err?.body.errorMessage, ToastLevel.ERROR);
      this.formApi.setEditing(true);
    });
  }

  private discard(): void {
    applicationContext.redirect(ApplicationRoute.DIAGNOSIS_LIST);
  }

  private cancelDiscard(record: Record<string, Value>): void {
    this.formApi.setDisabled(false);
    this.formApi.setData({
      ...record
    });
    this.formApi.setEditing(true);
  }

  @OnClick(DiagnosisEditorAliases.BUTTON_RELEASE)
  handleClickButtonRelease(): void {
    const attendanceNumber = this.context.get('encounterId') as string;
    const personCode = this.context.get('patientId') as string;
    const sequenceNumber = this.context.get('sequenceNumber') as string;
    this.diagnosisService.release(personCode, sequenceNumber, attendanceNumber);
  }

  @OnClick(DiagnosisEditorAliases.ACTION_NEW_DIAGNOSIS)
  handleClickActionNewDiagnosis(): void {
    this.onNew();
  }

  @OnClick(DiagnosisEditorAliases.ACTION_DELETE_DIAGNOSIS)
  handleClickActionDeleteDiagnosis(): void {
    this.onDelete();
  }

  @OnMount(DiagnosisEditorAliases.ACTION_NEW_DIAGNOSIS)
  handleMountActionNewDiagnosis(event: MountEvent): void {
    const actionNewDiagnosis = event.target as ActionAPI;
    actionNewDiagnosis.setEnabled(true);
  }
}
